// src/components/UserDetails.jsx
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Avatar,
  IconButton,
  Modal,
  Backdrop,
  Fade,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip
} from '@mui/material';
import { jwtDecode } from 'jwt-decode';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import CakeIcon from '@mui/icons-material/Cake';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SecurityIcon from '@mui/icons-material/Security';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import FaceIcon from '@mui/icons-material/Face';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const INVALID_VALUES = new Set([
  'UNKNOWN', 'WFFWFW', 'ASWW', 'TEST', 'ALL', 'UNDEFINED', 'NULL', ''
]);

const getAvatarColor = (name = '') => {
  const colors = ['#0284c7', '#0f766e', '#7c3aed', '#ea580c', '#2563eb', '#059669', '#d97706'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
  return colors[Math.abs(hash) % colors.length];
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return String(dateStr);
  }
};

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '94%', sm: '580px', md: '640px' },
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: '#ffffff',
  borderRadius: 3,
  boxShadow: '0 24px 48px rgba(15, 23, 42, 0.25)',
  p: { xs: 2.5, md: 3.5 },
  outline: 'none'
};

const UserDetails = () => {
  const token = localStorage.getItem('token');
  const decoded = useMemo(() => {
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }, [token]);

  // Normalized Role Check
  const normalizedRole = useMemo(() => {
    const r = String(decoded?.role || decoded?.userRole || '').trim().toLowerCase();
    if (r === 'poweruser' || r === 'power user' || decoded?.isPowerUser) return 'powerUser';
    if (r === 'admin') return 'admin';
    return 'user';
  }, [decoded]);

  const isPowerUser = normalizedRole === 'powerUser';
const [deleteError, setDeleteError] = useState('');
  const [employeeList, setEmployeeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal State
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Delete Dialog State
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const [filters, setFilters] = useState({
    empCode: '',
    name: '',
    divCode: '',
    projectCode: '',
    category: '',
    subContractor: ''
  });

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('https://gannondunkerley.in/employees', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = res.data.EmployeeList || res.data || [];
      setEmployeeList(list);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.msg || 'Failed to fetch employee records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Enforce Admin / User Project Scoping
  useEffect(() => {
    if (normalizedRole === 'admin' && decoded?.projectCode) {
      setFilters((prev) => ({ ...prev, projectCode: decoded.projectCode }));
    } else if (normalizedRole === 'user' && decoded?.empCode) {
      setFilters((prev) => ({ ...prev, empCode: decoded.empCode }));
    }
  }, [normalizedRole, decoded]);

  // Filter Dropdown options
  const uniqueCategories = useMemo(() => {
    return [...new Set(employeeList.map((e) => e.category).filter(Boolean))].sort();
  }, [employeeList]);

  const uniqueSubContractors = useMemo(() => {
    return [...new Set(employeeList.map((e) => e.subContractor).filter(Boolean))].sort();
  }, [employeeList]);

  const cleanDivCodes = useMemo(() => {
    const set = new Set();
    employeeList.forEach((e) => {
      const val = String(e.divCode || '').trim().toUpperCase();
      if (val && !INVALID_VALUES.has(val)) set.add(val);
    });
    return Array.from(set).sort();
  }, [employeeList]);

  const cleanProjectCodes = useMemo(() => {
    const set = new Set();
    employeeList.forEach((e) => {
      const val = String(e.projectCode || '').trim().toUpperCase();
      if (val && !INVALID_VALUES.has(val)) set.add(val);
    });
    return Array.from(set).sort();
  }, [employeeList]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category' && value !== 'Labour') {
      setFilters((prev) => ({ ...prev, category: value, subContractor: '' }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
    setPage(0);
  };

  const handleResetFilters = () => {
    setFilters({
      empCode: normalizedRole === 'user' ? (decoded?.empCode || '') : '',
      name: '',
      divCode: '',
      projectCode: normalizedRole === 'admin' ? (decoded?.projectCode || '') : '',
      category: '',
      subContractor: ''
    });
    setPage(0);
  };

  // Filtered List calculation
  const filteredEmployees = useMemo(() => {
    return employeeList.filter((emp) => {
      const empC = String(emp.empCode || '').toLowerCase();
      const empN = String(emp.name || '').toLowerCase();
      const empD = String(emp.divCode || '').toLowerCase();
      const empP = String(emp.projectCode || '').toLowerCase();
      const empCat = String(emp.category || '').toLowerCase();
      const empSub = String(emp.subContractor || '').toLowerCase();

      const matchEmpCode = empC.includes(filters.empCode.trim().toLowerCase());
      const matchName = empN.includes(filters.name.trim().toLowerCase());
      const matchDiv = filters.divCode ? empD === filters.divCode.toLowerCase() : true;
      const matchProj = filters.projectCode ? empP === filters.projectCode.toLowerCase() : true;
      const matchCat = filters.category ? empCat === filters.category.toLowerCase() : true;
      const matchSub = (filters.category === 'Labour' && filters.subContractor)
        ? empSub === filters.subContractor.toLowerCase()
        : true;

      return matchEmpCode && matchName && matchDiv && matchProj && matchCat && matchSub;
    });
  }, [employeeList, filters]);

  // Paginated Rows
  const paginatedEmployees = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredEmployees.slice(start, start + rowsPerPage);
  }, [filteredEmployees, page, rowsPerPage]);

  const handleOpenDetailModal = (emp) => {
    setSelectedEmp(emp);
    setOpenModal(true);
  };

  // Open Delete Confirmation
  const handlePromptDelete = (emp, e) => {
    e.stopPropagation();
    setDeleteTarget(emp);
    setDeleteDialogOpen(true);
  };

  // Confirm Delete Action (Deletes employee master record only, attendance remains in DB)
 const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');

    const targetEmpCode = String(deleteTarget.empCode || '').trim();

    try {
      // Backend route router.delete("/:empCode") mounted on /employees
      const res = await axios.delete(`https://gannondunkerley.in/employees/${targetEmpCode}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (res.status === 200) {
        // 1. State update
        setEmployeeList((prev) => prev.filter((item) => String(item.empCode).trim() !== targetEmpCode));
        setDeleteDialogOpen(false);
        setDeleteTarget(null);

        // 2. Server re-fetch confirmation
        await fetchEmployees();

        alert(res.data?.msg || `Employee #${targetEmpCode} (${deleteTarget.name}) successfully removed from master database!`);
      }
    } catch (err) {
      console.error('Delete employee error:', err);
      const serverMsg = err.response?.data?.msg || err.response?.data?.message || err.message || 'Server rejected delete request.';
      setDeleteError(`Delete failed: ${serverMsg}`);
    } finally {
      setDeleting(false);
    }
  };
  return (
    <Box className="content" sx={{ p: { xs: 2, md: 3.5 }, bgcolor: '#f4f6fa', minHeight: '100vh' }}>
      
      {/* TOP HEADER */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { md: 'center' },
        gap: 2,
        mb: 3
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
              Personnel & User Profiles
            </Typography>
            <Chip
              label={isPowerUser ? 'Power User (Full Control)' : normalizedRole === 'admin' ? 'Project Admin' : 'Staff View'}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.72rem',
                bgcolor: isPowerUser ? '#ede9fe' : '#e0e7ff',
                color: isPowerUser ? '#6d28d9' : '#4338ca',
                borderRadius: 1.5
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Master employee registry, KYC details, hierarchy mapping & active enrollment status
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchEmployees}
          disabled={loading}
          sx={{
            bgcolor: '#ffffff',
            borderRadius: 2,
            borderColor: '#cbd5e1',
            color: '#334155',
            fontWeight: 700,
            textTransform: 'none',
            px: 2,
            py: 0.8,
            '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' }
          }}
        >
          Reload Records
        </Button>
      </Box>

      {/* FILTER SEARCH CRITERIA */}
      <Paper elevation={0} sx={{
        p: 3,
        mb: 3,
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        bgcolor: '#ffffff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterAltIcon sx={{ color: '#0284c7', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
              Filter Personnel Records
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={handleResetFilters}
            sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b' }}
          >
            Clear Filters
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* Emp Code */}
          <TextField
            size="small"
            label="Emp Code"
            name="empCode"
            placeholder="e.g. 115073"
            value={filters.empCode}
            onChange={handleFilterChange}
            disabled={normalizedRole === 'user'}
            sx={{ minWidth: 160, flex: '1 1 160px', bgcolor: '#fff' }}
          />

          {/* Name */}
          <TextField
            size="small"
            label="Employee Name"
            name="name"
            placeholder="Search by name..."
            value={filters.name}
            onChange={handleFilterChange}
            sx={{ minWidth: 190, flex: '1 1 190px', bgcolor: '#fff' }}
          />

          {/* Division */}
          <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 160px', bgcolor: '#fff' }}>
            <InputLabel>Division Code</InputLabel>
            <Select
              name="divCode"
              value={filters.divCode}
              label="Division Code"
              onChange={handleFilterChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All Divisions</MenuItem>
              {cleanDivCodes.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Project Code */}
          {normalizedRole === 'admin' ? (
            <TextField
              size="small"
              label="Project Code"
              value={decoded?.projectCode || ''}
              disabled
              sx={{ minWidth: 170, flex: '1 1 170px', bgcolor: '#f8fafc' }}
            />
          ) : (
            <FormControl size="small" sx={{ minWidth: 170, flex: '1 1 170px', bgcolor: '#fff' }}>
              <InputLabel>Project Code</InputLabel>
              <Select
                name="projectCode"
                value={filters.projectCode}
                label="Project Code"
                onChange={handleFilterChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Projects</MenuItem>
                {cleanProjectCodes.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Category */}
          <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 160px', bgcolor: '#fff' }}>
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={filters.category}
              label="Category"
              onChange={handleFilterChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {uniqueCategories.map((cat) => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sub Contractor */}
          {filters.category === 'Labour' && (
            <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px', bgcolor: '#fff' }}>
              <InputLabel>Sub Contractor</InputLabel>
              <Select
                name="subContractor"
                value={filters.subContractor}
                label="Sub Contractor"
                onChange={handleFilterChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Contractors</MenuItem>
                {uniqueSubContractors.map((sc) => (
                  <MenuItem key={sc} value={sc}>{sc}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Paper>

      {/* ERROR ALERT */}
      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: '#dc2626', fontWeight: 600 }}>{error}</Typography>
        </Paper>
      )}

      {/* LOADING */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <CircularProgress size={36} sx={{ color: '#0284c7' }} />
          <Typography variant="body2" sx={{ color: '#64748b', mt: 2, fontWeight: 600 }}>
            Querying employee registry...
          </Typography>
        </Box>
      )}

      {/* MAIN TABLE REGISTER */}
      {!loading && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 620 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 800, color: '#334155', py: 1.5 } }}>
                  <TableCell>Emp Code</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Division</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Sub Contractor</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone No</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                      No personnel records match your criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedEmployees.map((emp, idx) => {
                    const isLabour = String(emp.category).toLowerCase() === 'labour';

                    return (
                      <TableRow
                        key={idx}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '&:nth-of-type(even)': { bgcolor: '#fafafa' },
                          '&:last-child td': { border: 0 }
                        }}
                        onClick={() => handleOpenDetailModal(emp)}
                      >
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                          {emp.empCode}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>
                          {emp.name || 'Staff Member'}
                        </TableCell>
                        <TableCell>
                          <Chip label={emp.projectCode || 'N/A'} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.72rem' }} />
                        </TableCell>
                        <TableCell sx={{ color: '#475569', fontWeight: 600 }}>
                          {emp.divCode}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={emp.category || 'General'}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              bgcolor: isLabour ? '#fff7ed' : '#ecfdf5',
                              color: isLabour ? '#c2410c' : '#059669'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#64748b' }}>
                          {emp.subContractor || '—'}
                        </TableCell>
                        <TableCell sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                          {emp.email || '—'}
                        </TableCell>
                        <TableCell sx={{ color: '#475569', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                          {emp.phoneNumber || '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={emp.role || 'User'}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              bgcolor: '#f1f5f9',
                              color: '#334155'
                            }}
                          />
                        </TableCell>

                        {/* Action Column: View Dossier & PowerUser Delete */}
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<VisibilityIcon fontSize="small" />}
                              onClick={() => handleOpenDetailModal(emp)}
                              sx={{ textTransform: 'none', fontWeight: 700, color: '#0284c7' }}
                            >
                              View ID
                            </Button>

                            {/* Power User Delete Option for Offboarded Staff */}
                            {isPowerUser && (
                              <Tooltip title="Offboard / Remove Employee (Keeps attendance in DB)">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handlePromptDelete(emp, e)}
                                  sx={{ color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[15, 30, 60, 100]}
            component="div"
            count={filteredEmployees.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              borderTop: '1px solid #e2e8f0',
              bgcolor: '#ffffff',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontWeight: 600,
                color: '#64748b'
              }
            }}
          />
        </Paper>
      )}

      {/* --- DETAILED PERSONNEL ID DOSSIER MODAL (ALL MONGO FIELDS) --- */}
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 300,
            sx: { backdropFilter: 'blur(4px)', bgcolor: 'rgba(15, 23, 42, 0.5)' }
          }
        }}
      >
        <Fade in={openModal}>
          <Box sx={modalStyle}>
            {selectedEmp && (
              <>
                {/* Modal Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: getAvatarColor(selectedEmp.name),
                        width: 58,
                        height: 58,
                        fontWeight: 800,
                        fontSize: '1.25rem',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    >
                      {(selectedEmp.name || 'EM').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                        {selectedEmp.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={`EmpCode: #${selectedEmp.empCode}`}
                          size="small"
                          sx={{ fontFamily: 'monospace', fontWeight: 800, bgcolor: '#0284c7', color: '#fff', height: 22 }}
                        />
                        <Chip
                          label={selectedEmp.category || 'Employee'}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155', height: 22 }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <IconButton onClick={() => setOpenModal(false)} size="small" sx={{ bgcolor: '#f8fafc' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                {/* Grid Fields directly matching MongoDB Document */}
                <Grid container spacing={2}>
                  {/* Reporting Manager */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SupervisorAccountIcon sx={{ fontSize: 14, color: '#0284c7' }} /> REPORTING MANAGER
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                        {selectedEmp.reportingManager ? `#${selectedEmp.reportingManager}` : 'None Assigned'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Project & Division */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BusinessIcon sx={{ fontSize: 14, color: '#0284c7' }} /> PROJECT & DIVISION
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                        {selectedEmp.projectCode || 'Unassigned'} ({selectedEmp.divCode || 'KOL'})
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Phone Number */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: '#16a34a' }} /> PRIMARY PHONE
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontFamily: 'monospace' }}>
                        {selectedEmp.phoneNumber || 'Not Registered'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Emergency Number */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ContactPhoneIcon sx={{ fontSize: 14, color: '#dc2626' }} /> EMERGENCY CONTACT
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontFamily: 'monospace' }}>
                        {selectedEmp.emergencyNumber || 'Not Registered'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Corporate Email */}
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 14, color: '#0284c7' }} /> EMAIL ADDRESS
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                        {selectedEmp.email || '—'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Aadhar Card */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CreditCardIcon sx={{ fontSize: 14, color: '#7c3aed' }} /> AADHAAR NUMBER
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontFamily: 'monospace' }}>
                        {selectedEmp.aadhar ? `${selectedEmp.aadhar.slice(0, 4)} **** ${selectedEmp.aadhar.slice(-4)}` : '—'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* PAN Card */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CreditCardIcon sx={{ fontSize: 14, color: '#7c3aed' }} /> PAN CARD
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontFamily: 'monospace' }}>
                        {selectedEmp.panCard || '—'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Father's Name */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FamilyRestroomIcon sx={{ fontSize: 14, color: '#475569' }} /> FATHER'S NAME
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                        {selectedEmp.fathersName || '—'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Date of Birth */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CakeIcon sx={{ fontSize: 14, color: '#ec4899' }} /> DATE OF BIRTH
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                        {formatDate(selectedEmp.dob)}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Sub Contractor */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                        SUB CONTRACTOR
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                        {selectedEmp.subContractor || 'Direct GDCL Payroll'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Biometric Face Descriptors Status */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FaceIcon sx={{ fontSize: 14, color: '#0284c7' }} /> FACE BIOMETRIC
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                          {selectedEmp.faceDescriptors && selectedEmp.faceDescriptors.length > 0 ? 'Enrolled (Active)' : 'Not Enrolled'}
                        </Typography>
                      </Box>
                      <Chip
                        label={selectedEmp.faceDescriptors && selectedEmp.faceDescriptors.length > 0 ? 'Verified' : 'Pending'}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: selectedEmp.faceDescriptors && selectedEmp.faceDescriptors.length > 0 ? '#ecfdf5' : '#fffbeb',
                          color: selectedEmp.faceDescriptors && selectedEmp.faceDescriptors.length > 0 ? '#059669' : '#b45309'
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>

                {/* Modal Footer */}
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                    Document ID: {selectedEmp._id}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setOpenModal(false)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 700,
                      bgcolor: '#0f172a',
                      '&:hover': { bgcolor: '#1e293b' }
                    }}
                  >
                    Close Dossier
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* --- CONFIRMATION DIALOG: OFFBOARD / DELETE USER (POWERUSER ONLY) --- */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => { if (!deleting) setDeleteDialogOpen(false); }}
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 440 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#991b1b', fontWeight: 800 }}>
          <WarningAmberIcon color="error" /> Confirm Employee Removal
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#334155', fontSize: '0.92rem', lineHeight: 1.6 }}>
            Are you sure you want to offboard and remove employee{' '}
            <strong>{deleteTarget?.name} (#{deleteTarget?.empCode})</strong>?
          </DialogContentText>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fef2f2', borderRadius: 2, border: '1px solid #fecaca' }}>
            <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 600 }}>
              Audit Notice: Removing this profile deletes their login and master enrollment, but all past attendance logs and biometric records will remain safely preserved in the database.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleting}
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
          >
            {deleting ? 'Removing...' : 'Confirm Offboard'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default UserDetails;